import type { ReactNode } from 'react';

import type { MultiSelectFilterValue } from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { FormContextConsumer } from '../../form_context/form_context';
import formField, { pickProps } from '../../form_field/form_field';
import type { FormFieldEvent } from '../../types';
import { MultiSelectFilterField } from './multi_select_filter_field';
import type { MultiSelectFilterFieldProps } from './types';

import styles from './multi_select_filter_field_wrapper.module.css';

const componentProps = [
  'loadOptions',
  'resolveSelectedItems',
  'itemLabels',
  'renderItemChip',
  'availableModifiers',
  'maxRows',
  'showItemAvatar',
  'showStartTypingHint',
  'startTypingHint',
] as const;

const noFilterGroups: MultiSelectFilterValue = [];

const MultiSelectFilterFormField = formField(MultiSelectFilterField, {
  defaultValue: noFilterGroups,
  transformProps: (props) => pickProps(props, componentProps),
});

export type MultiSelectFilterFieldWrapperProps = Omit<
  MultiSelectFilterFieldProps,
  'value' | 'onChange'
> & {
  name: string;
  value?: MultiSelectFilterValue;
  onChange?: (
    value: MultiSelectFilterValue | undefined,
    event?: FormFieldEvent,
  ) => void;
  label?: string;
  subtitle?: string;
  // Applied by the formField HOC before storing the value in the form model.
  filterValue?: (
    value: MultiSelectFilterValue | undefined,
  ) => MultiSelectFilterValue | undefined;
  vertical?: boolean;
};

export function MultiSelectFilterFieldWrapper({
  subtitle,
  label,
  value,
  ...props
}: MultiSelectFilterFieldWrapperProps): ReactNode {
  const hasSelection = value?.some((group) => group.items.length > 0) ?? false;

  const composedLabel =
    subtitle && label && hasSelection ? (
      <>
        {label}
        <span className={styles.subtitle}>{subtitle}</span>
      </>
    ) : (
      label
    );

  return (
    <div role="group" aria-label={label}>
      <FormContextConsumer>
        {(context) => (
          <MultiSelectFilterFormField
            {...props}
            value={value}
            label={composedLabel}
            {...context}
          />
        )}
      </FormContextConsumer>
    </div>
  );
}
